/**
 * The client-side reading of a feed-quality-stats response (`getFeedQualityStats`
 * / `getOrgFeedQualityStats` in ./stock.js), shared by every surface that
 * turns the counts into a percentage so they all agree on the denominator.
 *
 * `inStockItems` is our own in-stock count and the only field that means the
 * same thing whether the active / error counts come from Merchant Centre or
 * from our own index — `totalItems` on a Google response adds Merchant Centre
 * offers to our rows, and no ratio built on it means anything (NEA-6601).
 *
 * The `??` fallbacks read a response cached before `inStockItems` /
 * `erroneousItems` shipped the way the consumers always did, so a stale cache
 * can't blank a card.
 */
export const errorsFor = (stats) => stats.erroneousItems ?? Math.max(0, stats.totalItems - stats.activeItems - stats.inactiveItems);

export const inStockFor = (stats) => stats.inStockItems ?? stats.activeItems + errorsFor(stats);

/** In stock but neither active nor errored: what the channel is not listing yet. */
export const notShowingFor = (stats) => Math.max(0, inStockFor(stats) - stats.activeItems - errorsFor(stats));

/**
 * Active listings beyond our in-stock count: how far the channel's active row
 * sits above our headline. Only a channel that counts its own listings
 * (Google) can run ahead of us: Merchant Centre keeps a product for 30 days
 * after its last refresh, and nothing refreshes a product once it leaves
 * stock, so for up to a month after a sell-out Google still lists it.
 * Disapproved products are deliberately left out — Google holds them but
 * does not list them, and the figure sits beside the active count. For our
 * own numbers this is zero by construction.
 */
export const surplusFor = (stats) => Math.max(0, stats.activeItems - inStockFor(stats));
